import type { ReactNode } from "react";
import { ChevronRightIcon } from "./Icons.tsx";
import { Switch } from "./Switch.tsx";

/**
 * One line in a grouped list: a participant, a business, a package on sale.
 *
 * The whole row is the target, not a chevron at the end of it. On a phone the difference between a
 * 350px tap target and a 24px one is the difference between a list that works while walking and one
 * that does not.
 *
 * A row with no `onSelect` renders as plain content rather than a dead button, so a list can mix
 * the two without anyone having to guess which lines do something.
 *
 * `details` is for a row that must be read in full before a tap, such as a package in the shop: the
 * title wraps instead of being cut, the details sit under it, and the row aligns to the top.
 */
export function ListRow({
  title,
  subtitle,
  value,
  trailing,
  onSelect,
  disabled = false,
  chevron = true,
  badge,
  details,
}: {
  title: string;
  subtitle?: string;
  /** A small tag above the title, such as the shop's "New client offer". */
  badge?: ReactNode;
  /** Right-aligned value, for a price or a count. */
  value?: string;
  /** Anything richer than a value: a badge, a tick, a switch. */
  trailing?: ReactNode;
  onSelect?: () => void;
  disabled?: boolean;
  chevron?: boolean;
  /** Content under the title, shown in full (a list of facts). The title wraps when it is set. */
  details?: ReactNode;
}) {
  const body = (
    <>
      <span className="min-w-0 flex-1">
        {badge ? <span className="mb-1 flex">{badge}</span> : null}
        <span
          dir="auto"
          className={`block text-left text-[15px] font-semibold text-ink ${details ? "break-words" : "truncate"}`}
        >
          {title}
        </span>
        {subtitle ? (
          <span dir="auto" className="mt-0.5 block truncate text-left text-[13px] text-ink-2">
            {subtitle}
          </span>
        ) : null}
        {details}
      </span>
      {value ? (
        <span className="shrink-0 text-[15px] font-semibold text-ink tabular-nums">{value}</span>
      ) : null}
      {trailing}
      {onSelect && chevron ? (
        <span className="shrink-0 text-ink-3">
          <ChevronRightIcon size={18} />
        </span>
      ) : null}
    </>
  );

  const shell = `flex min-h-14 w-full ${details ? "items-start" : "items-center"} gap-3 py-3 text-left`;

  if (!onSelect) return <div className={shell}>{body}</div>;

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      className={`${shell} -mx-1 rounded-lg px-1 transition-colors duration-150 active:bg-sunken disabled:pointer-events-none disabled:opacity-45`}
    >
      {body}
    </button>
  );
}

/**
 * A row that is a switch.
 *
 * Built on a real checkbox rather than a styled div, so it is reachable by keyboard and announced
 * as a switch, and so the label toggles it: the whole row is the target, same as `ListRow`. The
 * switch itself is `Switch`, the one switch recipe.
 */
export function ToggleRow({
  label,
  hint,
  checked,
  onChange,
  disabled = false,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className="flex min-h-14 cursor-pointer items-center gap-3 py-3 has-disabled:cursor-not-allowed">
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] font-semibold text-ink">{label}</span>
        {hint ? <span className="mt-0.5 block text-[13px] text-ink-2">{hint}</span> : null}
      </span>
      <Switch checked={checked} onChange={onChange} disabled={disabled} />
    </label>
  );
}

/**
 * The card a list of rows sits in.
 *
 * The divider is drawn by the list rather than by each row, so the first and last lines never end
 * up with a stray rule against the card edge.
 */
export function ListCard({ children, footer }: { children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="rounded-card border border-line bg-paper px-4 shadow-card">
      <div className="divide-y divide-line">{children}</div>
      {footer ? <div className="border-t border-line py-3.5">{footer}</div> : null}
    </div>
  );
}
